import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageLoader, Spinner, Countdown } from "@/components/Kit";
import { playSfx } from "@/lib/sound";
import { Compass, Coins, Check, Star, Lock } from "lucide-react";

function QuestCard({ q, onClaim, busy }) {
  const pct = Math.min(100, (q.progress / q.target) * 100);
  const locked = q.scope === "story" && !q.active && !q.claimed;
  return (
    <div data-testid={`quest-${q.id}`} className={`panel p-4 ${q.progress >= q.target && !q.claimed ? "panel-glow" : ""}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-head font-bold text-sm">{q.title}</h3>
        {q.scope === "story" && q.grant_scl && <span className="label-cap text-[9px] text-primary">→ SCL-{q.grant_scl}</span>}
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 mb-2">{q.desc}</p>
      <div className="h-1.5 rounded bg-muted overflow-hidden mb-2"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
      <div className="flex items-center justify-between">
        <span className="label-cap flex items-center gap-1"><Coins className="w-3 h-3 text-secondary" />{q.reward} · {Math.min(q.progress, q.target)}/{q.target}</span>
        {q.claimed ? <span className="text-green-500 flex items-center gap-1 text-xs font-bold"><Check className="w-4 h-4" />Done</span>
          : locked ? <span className="text-muted-foreground flex items-center gap-1 text-xs"><Lock className="w-3.5 h-3.5" />Locked</span>
          : <button onClick={() => onClaim(q)} data-testid={`claim-${q.id}`} disabled={q.progress < q.target || busy === q.id} className={q.progress >= q.target ? "btn-primary text-xs px-3 py-1.5" : "btn-ghost text-xs px-3 py-1.5 opacity-60"}>{busy === q.id ? <Spinner className="w-3 h-3" /> : "Claim"}</button>}
      </div>
    </div>
  );
}

export default function Quests() {
  const { patchUser, refreshUser } = useOutletContext();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/quests").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <PageLoader label="Loading missions" />;

  const claim = async (q) => {
    setBusy(q.id);
    try { const { data: res } = await api.post(`/quests/${q.scope}/${q.id}/claim`); patchUser({ credits: res.credits, scl: res.scl }); playSfx("coin");
      toast.success(`+${res.reward} Credits${q.grant_scl ? ` · Clearance raised to SCL-${res.scl}!` : ""}`); if (refreshUser) await refreshUser(); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };

  const Section = ({ title, icon: Icon, items }) => (
    <div className="mb-8">
      <h2 className="font-head font-bold text-lg mb-3 flex items-center gap-2"><Icon className="w-5 h-5 text-primary" />{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{items.map((q) => <QuestCard key={q.id} q={q} onClaim={claim} busy={busy} />)}</div>
    </div>
  );

  return (
    <div data-testid="quests-page">
      <span className="label-cap text-primary flex items-center gap-2"><Compass className="w-4 h-4" /> Mission Control</span>
      <h1 className="text-4xl font-black tracking-tighter mt-2 mb-6 font-head">Quest Terminal</h1>
      <Section title="Story Missions" icon={Star} items={data.story} />
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-head font-bold text-lg flex items-center gap-2"><Compass className="w-5 h-5 text-primary" />Daily</h2>
        <span className="label-cap text-secondary">resets in <Countdown seconds={data.daily_reset_in} onDone={load} testId="daily-reset-timer" /></span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">{data.daily.map((q) => <QuestCard key={q.id} q={q} onClaim={claim} busy={busy} />)}</div>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-head font-bold text-lg flex items-center gap-2"><Compass className="w-5 h-5 text-primary" />Weekly</h2>
        <span className="label-cap text-secondary">resets in <Countdown seconds={data.weekly_reset_in} onDone={load} testId="weekly-reset-timer" /></span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{data.weekly.map((q) => <QuestCard key={q.id} q={q} onClaim={claim} busy={busy} />)}</div>
    </div>
  );
}
